/**
 * Flag dismiss/restore UI — split into two pieces so the reason editor
 * can be positioned independently of (i.e. below) the flag it belongs
 * to:
 *
 * - `FlagDismissButton` — the small inline button rendered next to a
 *   flag badge. Dismiss (opens the editor), restore, or a read-only
 *   info icon depending on flag state and the user's permissions.
 * - `FlagDismissEditor` — the reason editor. Render it directly beneath
 *   the flag; it only shows itself when its flag's dismiss button has
 *   been clicked.
 *
 * The editor is plain inline markup (not a portalled popover) so it
 * works inside focus-trapping slide-over panels.
 */

import { useState } from "react";
import { toast } from "sonner";
import { Ban, RotateCcw, Info, Loader2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/ui/composed/overlays/tooltip";
import { cn } from "@/utils/misc";
import { flagKey, type FlagAgentKind } from "./flagKey";
import type { UseFlagDismissalsResult } from "./useFlagDismissals";

interface FlagIdentity {
  ctx: UseFlagDismissalsResult;
  agentKind: FlagAgentKind;
  entryId: string;
  flagType: string;
  /** Detail text feeding the stable key. */
  flagDetail: string;
}

function fmtWhen(ts: number): string {
  try {
    return new Date(ts).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

export function FlagDismissButton({
  ctx,
  agentKind,
  entryId,
  flagType,
  flagDetail,
  className,
}: FlagIdentity & { className?: string }) {
  const key = flagKey(agentKind, entryId, flagType, flagDetail);
  const dismissal = ctx.getDismissal(agentKind, entryId, {
    type: flagType,
    detail: flagDetail,
  });

  // --- Dismissed -----------------------------------------------------------
  if (dismissal) {
    const detail = (
      <span className="text-xs">
        Dismissed by {dismissal.dismissedByName ?? "a practitioner"} on{" "}
        {fmtWhen(dismissal.dismissedAt)}
        {dismissal.reason ? ` — ${dismissal.reason}` : ""}
      </span>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {ctx.canManage ? (
            <button
              type="button"
              aria-label="Restore flag"
              disabled={ctx.isPending}
              onClick={(e) => {
                e.stopPropagation();
                ctx
                  .restore({
                    agentKind,
                    entryId,
                    flag: { type: flagType, detail: flagDetail },
                  })
                  .then(() => toast.success("Flag restored"))
                  .catch((err: unknown) =>
                    toast.error(
                      err instanceof Error
                        ? err.message
                        : "Failed to restore flag",
                    ),
                  );
              }}
              className={cn(
                "inline-flex items-center justify-center rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                className,
              )}
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          ) : (
            <span
              className={cn(
                "inline-flex items-center justify-center text-muted-foreground",
                className,
              )}
            >
              <Info className="h-3 w-3" />
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent>
          {ctx.canManage ? (
            <span className="flex flex-col gap-0.5">
              {detail}
              <span className="text-[10px] opacity-70">
                Click to restore this flag
              </span>
            </span>
          ) : (
            detail
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  // --- Active --------------------------------------------------------------
  if (!ctx.canManage) return null;

  const isEditing = ctx.editingKey === key;
  return (
    <button
      type="button"
      aria-label="Dismiss flag"
      onClick={(e) => {
        e.stopPropagation();
        if (isEditing) ctx.stopEditing();
        else ctx.startEditing(key);
      }}
      className={cn(
        "inline-flex items-center justify-center rounded p-0.5 transition-colors",
        isEditing
          ? "bg-rose-50 text-rose-600 dark:bg-rose-950/30"
          : "text-muted-foreground hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30",
        className,
      )}
    >
      <Ban className="h-3 w-3" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export function FlagDismissEditor({
  ctx,
  agentKind,
  entryId,
  entryLabel,
  flagType,
  flagLabel,
  flagDetail,
  className,
}: FlagIdentity & {
  entryLabel: string;
  flagLabel: string;
  className?: string;
}) {
  const key = flagKey(agentKind, entryId, flagType, flagDetail);
  const [reason, setReason] = useState("");

  // Only the flag whose button was clicked renders its editor.
  if (ctx.editingKey !== key) return null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "mt-1.5 w-full rounded-md border border-rose-200 bg-background p-3 shadow-sm dark:border-rose-900",
        className,
      )}
    >
      <p className="text-sm font-medium text-foreground">
        Dismiss “{flagLabel}” flag
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Clears the alert on “{entryLabel}”. You can restore it later.
      </p>
      <Textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional) — recorded in job history"
        className="mt-2 min-h-[64px] text-sm"
        autoFocus
      />
      <div className="mt-2 flex justify-end gap-2">
        <Button
          size="sm"
          type="button"
          variant="outline"
          onClick={() => {
            setReason("");
            ctx.stopEditing();
          }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="button"
          disabled={ctx.isPending}
          onClick={async () => {
            const payload = {
              agentKind,
              entryId,
              entryLabel,
              flagType,
              flagLabel,
              flagDetail,
              reason: reason.trim() || undefined,
            };
            try {
              await ctx.dismiss(payload);
              toast.success("Flag dismissed");
              setReason("");
              ctx.stopEditing();
            } catch (err: unknown) {
              console.error("[flag] dismiss failed", err);
              toast.error(
                err instanceof Error ? err.message : "Failed to dismiss flag",
              );
            }
          }}
          className="gap-1.5"
        >
          {ctx.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          Dismiss
        </Button>
      </div>
    </div>
  );
}
